import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    Container,
    Stack,
    Typography,
    Card,
    Box,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Button,
} from '@mui/material'
import QrReader from "react-qr-reader-es6";
import api from '../../api';
import { toast } from 'react-toastify';

export default function ScanPackage() {
    const [adminLoading, setAdminLoading] = useState(true);
    const [adminCity, setAdminCity] = useState('');
    const [adminDataIncomplete, setAdminDataIncomplete] = useState(false);
    const [profileDialogOpen, setProfileDialogOpen] = useState(false);
    const [profileSaving, setProfileSaving] = useState(false);
    const [profileForm, setProfileForm] = useState({
        password: '',
        name: '',
        gstNo: '',
        address: '',
        city: '',
        district: '',
        state: '',
        country: '',
        contactNo: '',
        whatsappNo: '',
        pinCode: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [lastScanned, setLastScanned] = useState('');
    const lastScanAtRef = useRef(0);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [statusDraft, setStatusDraft] = useState('');
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    const statusOptions = useMemo(() => {
        if (!adminCity) return [];
        return [
            `arrived at hub (${adminCity})`,
            `departed from hub (${adminCity})`,
            `out of delivery (${adminCity})`,
        ];
    }, [adminCity]);

    const fetchAdminDetail = async () => {
        setAdminLoading(true);
        try {
            const response = await api.get('/api/v1/admin_detail');
            const data = response?.data || {};
            const admin = data?.admin || {};
            const adminDetail = data?.adminDetail || data?.admin_detail || {};
            const city =
                admin?.city ||
                adminDetail?.city ||
                data?.city ||
                '';

            setProfileForm((prev) => ({
                ...prev,
                name: admin?.name || '',
                gstNo: adminDetail?.gstNo || '',
                address: adminDetail?.address || '',
                city: adminDetail?.city || '',
                district: adminDetail?.district || '',
                state: adminDetail?.state || '',
                country: adminDetail?.country || '',
                contactNo: adminDetail?.contactNo || '',
                whatsappNo: adminDetail?.whatsappNo || '',
                pinCode: adminDetail?.pinCode || '',
            }));

            if (!city) {
                setAdminDataIncomplete(true);
                toast.error('Admin details are incomplete. Please update profile first.');
                return;
            }

            setAdminCity(city);
            setAdminDataIncomplete(false);
            setStatusDialogOpen(true);
        } catch (error) {
            setAdminDataIncomplete(true);
            toast.error(error?.response?.data?.message || error?.message || 'Failed to fetch admin details.');
        } finally {
            setAdminLoading(false);
        }
    };

    useEffect(() => {
        fetchAdminDetail();
    }, []);

    const handleScan = async (data) => {
        if (!data || isSubmitting || !selectedStatus) {
            return;
        }

        // Prevent multiple submissions from the same frame burst.
        const now = Date.now();
        if (now - lastScanAtRef.current < 150) {
            return;
        }

        const rawValue = typeof data === 'string' ? data : data?.text;
        const consignmentNo = String(rawValue || '').trim();
        if (!consignmentNo) {
            return;
        }

        try {
            setIsSubmitting(true);
            lastScanAtRef.current = now;
            const response = await api.post('/api/v1/scan_packages', {
                data: {
                    consignmentNo,
                    status: selectedStatus
                }
            });

            if (response?.data?.success) {
                setLastScanned(consignmentNo);
                toast.success(response?.data?.message || 'Package scanned successfully.');
            } else {
                toast.error(response?.data?.message || 'Failed to scan package.');
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || 'Failed to scan package.');
        } finally {
            setIsSubmitting(false);
        }
    };
    
      const handleError = (err) => {
        console.error(err);
      };

    const onClickOpenStatusDialog = () => {
        setStatusDraft(selectedStatus || '');
        setStatusDialogOpen(true);
    };

    const onClickStatusNext = () => {
        if (!statusDraft) {
            toast.error('Please choose status.');
            return;
        }
        setConfirmDialogOpen(true);
    };

    const onClickConfirmStatus = () => {
        setSelectedStatus(statusDraft);
        setConfirmDialogOpen(false);
        setStatusDialogOpen(false);
        toast.success('Status confirmed. Scanner is ready.');
    };

    const onChangeProfile = (event) => {
        const { name, value } = event.target;
        setProfileForm((prev) => ({ ...prev, [name]: value }));
    };

    const onClickSaveProfile = async () => {
        const requiredFields = ['password', 'name', 'gstNo', 'address', 'city', 'district', 'state', 'country', 'contactNo', 'whatsappNo', 'pinCode'];
        const hasMissing = requiredFields.some((field) => !profileForm[field] || String(profileForm[field]).trim() === '');
        if (hasMissing) {
            toast.error('Please fill all profile fields.');
            return;
        }

        setProfileSaving(true);
        try {
            const response = await api.put('/api/v1/admin_detail', {
                data: {
                    password: profileForm.password,
                    name: profileForm.name,
                    gstNo: profileForm.gstNo,
                    address: profileForm.address,
                    city: profileForm.city,
                    district: profileForm.district,
                    state: profileForm.state,
                    country: profileForm.country,
                    contactNo: profileForm.contactNo,
                    whatsappNo: profileForm.whatsappNo,
                    pinCode: profileForm.pinCode
                }
            });

            if (response?.data?.success) {
                setAdminCity(profileForm.city);
                setAdminDataIncomplete(false);
                setProfileDialogOpen(false);
                setStatusDialogOpen(true);
                toast.success(response?.data?.message || 'Profile updated successfully.');
            } else {
                toast.error(response?.data?.message || 'Failed to update profile.');
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || 'Failed to update profile.');
        } finally {
            setProfileSaving(false);
        }
    };

    return (
        <>
            <Container maxWidth="sm">
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                    <Typography variant="h4" gutterBottom>
                        Scan Package
                    </Typography>
                </Stack>

                <Card sx={{ p: { xs: 2, sm: 3 } }}>
                    <Stack spacing={2}>
                        <Typography variant="h6">QR Code Scanner</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Use your phone&apos;s back camera and place the QR code inside the frame.
                        </Typography>
                        <Alert severity={selectedStatus ? 'info' : 'warning'}>
                            <b>City:</b> {adminCity || 'NA'}<br />
                            <b>Selected Status:</b> {selectedStatus || 'Not selected'}
                        </Alert>
                        {adminDataIncomplete && (
                            <Alert
                                severity="error"
                                action={
                                    <Button color="inherit" size="small" onClick={() => setProfileDialogOpen(true)}>
                                        Update Profile
                                    </Button>
                                }
                            >
                                Admin city/details are missing. Please update profile first.
                            </Alert>
                        )}
                        <Button variant="outlined" onClick={onClickOpenStatusDialog} disabled={adminLoading}>
                            Choose / Change Status
                        </Button>

                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: 420,
                                mx: 'auto',
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'common.black',
                                opacity: selectedStatus && !adminDataIncomplete ? 1 : 0.55,
                            }}
                        >
                            <QrReader
                                delay={250}
                                onError={handleError}
                                onScan={selectedStatus && !adminDataIncomplete ? handleScan : () => {}}
                                constraints={{ facingMode: { ideal: 'environment' } }}
                                style={{ width: "100%" }}
                            />
                        </Box>

                        {adminLoading && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={18} />
                                <Typography variant="body2" color="text.secondary">
                                    Loading admin details...
                                </Typography>
                            </Stack>
                        )}

                        {isSubmitting && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={18} />
                                <Typography variant="body2" color="text.secondary">
                                    Submitting scan...
                                </Typography>
                            </Stack>
                        )}

                        {lastScanned && (
                            <Alert severity="success">
                                Last scanned consignment: <b>{lastScanned}</b>
                            </Alert>
                        )}
                    </Stack>
                </Card>

                <Dialog open={statusDialogOpen} disableEscapeKeyDown>
                    <DialogTitle>Select Scan Status</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1, minWidth: { xs: 260, sm: 360 } }}>
                            <Typography variant="body2" color="text.secondary">
                                Choose a status before scanning.
                            </Typography>
                            <TextField
                                select
                                label="Status"
                                value={statusDraft}
                                onChange={(e) => setStatusDraft(e.target.value)}
                                fullWidth
                            >
                                {statusOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClickStatusNext} variant="contained">
                            Next
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={confirmDialogOpen}>
                    <DialogTitle>Confirm Selected Status</DialogTitle>
                    <DialogContent>
                        <Typography variant="body1">
                            You have chosen <b>{statusDraft}</b>, please confirm.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmDialogOpen(false)} variant="outlined">
                            Back
                        </Button>
                        <Button onClick={onClickConfirmStatus} variant="contained">
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={profileDialogOpen} fullWidth maxWidth="sm">
                    <DialogTitle>Update Profile</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField name="name" label="Name" value={profileForm.name} onChange={onChangeProfile} fullWidth />
                            <TextField name="gstNo" label="GST No" value={profileForm.gstNo} onChange={onChangeProfile} fullWidth />
                            <TextField name="address" label="Address" value={profileForm.address} onChange={onChangeProfile} fullWidth />
                            <TextField name="city" label="City" value={profileForm.city} onChange={onChangeProfile} fullWidth />
                            <TextField name="district" label="District" value={profileForm.district} onChange={onChangeProfile} fullWidth />
                            <TextField name="state" label="State" value={profileForm.state} onChange={onChangeProfile} fullWidth />
                            <TextField name="country" label="Country" value={profileForm.country} onChange={onChangeProfile} fullWidth />
                            <TextField name="contactNo" label="Contact Number" value={profileForm.contactNo} onChange={onChangeProfile} fullWidth />
                            <TextField name="whatsappNo" label="Whatsapp Number" value={profileForm.whatsappNo} onChange={onChangeProfile} fullWidth />
                            <TextField name="pinCode" label="Pin Code" value={profileForm.pinCode} onChange={onChangeProfile} fullWidth />
                            <TextField
                                name="password"
                                label="Current Password (Confirm)"
                                type="password"
                                value={profileForm.password}
                                onChange={onChangeProfile}
                                fullWidth
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setProfileDialogOpen(false)} variant="outlined" disabled={profileSaving}>
                            Cancel
                        </Button>
                        <Button onClick={onClickSaveProfile} variant="contained" disabled={profileSaving}>
                            {profileSaving ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </>
    )
}
